'use client';

import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { ICandidate, ProviderProps } from '../types/types';
import { requestHandler } from '../utils/requestHandler';
import { useToast } from './toast-provider';
import { deleteCandidateRequest, getAllCandidatesRequest, getOneCandidateRequest, updateCandidateRequest } from '../utils/handlers/client/candidate';
import { useRouter } from 'next/navigation';
export const dynamic = 'force-dynamic';
// Create a context to manage Candidates-related data and functions
const CandidateContext = createContext({
  error: '',
  map: {},
  list: [],
  setList: list => {},
  setMap: map => {},
  metaData: {},
  adjustTotal: delta => {},
  getAllCandidates: async (query, page, limit, setIsLoading) => {},
  updateCandidate: async (id, data, setIsLoading) => {},
  deleteCandidate: async (id, data, setIsLoading) => {},
  getOneCandidate: async (id, data, setIsLoading) => {}
});

// Create a hook to access the CandidateContext
const useCandidateContext = () => useContext(CandidateContext);

// Create a component
const CandidateProvider = ({
  children
}) => {
  const {
    showErrorToast,
    showSuccessToast,
    showWarningToast
  } = useToast();
  const router = useRouter();
  const [map, setMap] = useState({});
  const [list, setList] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [error, setError] = useState('');
  const adjustTotal = delta => {
    setMetaData(prevMetaData => ({
      ...prevMetaData,
      total: typeof prevMetaData?.total === 'number' ? Math.max(prevMetaData.total + delta, 0) : prevMetaData?.total
    }));
  };

  // Function to handle get all Candidates
  const getAllCandidates = async (query, page = 1, limit = 10, setIsLoading, onError) => {
    await requestHandler(async () => await getAllCandidatesRequest({
      ...query,
      page,
      limit
    }), res => {
      const {
        data
      } = res;
      const {
        candidates = [],
        ...metaData
      } = data;
      const CandidateMap = candidates.reduce((map, data) => {
        map[data?.id] = data;
        return map;
      }, {});
      const CandidateIds = candidates.map(Candidate => Candidate?.id);
      setMap(CandidateMap);
      setList(CandidateIds);
      setMetaData(prevMetaData => ({
        ...prevMetaData,
        ...metaData,
        total: metaData?.total === null || metaData?.total === undefined ? prevMetaData?.total : metaData.total
      }));
    }, errorMsg => {
      showErrorToast(errorMsg);
      if (onError) onError(errorMsg);
    }, setIsLoading);
  };
  const updateCandidate = async (id, data, setIsLoading) => {
    await requestHandler(async () => await updateCandidateRequest(id, data), res => {
      const {
        data,
        message
      } = res;
      const CandidateId = data.id;
      setMap(prevMap => ({
        ...prevMap,
        [CandidateId]: data
      }));
      showSuccessToast(message);
    }, errMessage => {
      showErrorToast(errMessage);
    }, setIsLoading);
  };
  const deleteCandidate = async (id, data, setIsLoading) => {
    await requestHandler(async () => await deleteCandidateRequest(id, data), res => {
      const {
        data,
        message
      } = res;
      const CandidateId = data.id;
      setMap(prevMap => ({
        ...prevMap,
        [CandidateId]: null
      }));
      setList(prevList => prevList.filter(id => id !== CandidateId));
      adjustTotal(-1);
      showSuccessToast(message);
    }, errMessage => {
      showErrorToast(errMessage);
    }, setIsLoading);
  };
  const getOneCandidate = async (id, data, setIsLoading) => {
    await requestHandler(async () => await getOneCandidateRequest(id, data), res => {
      const {
        data,
        message
      } = res;
      const CandidateId = data.id;
      setMap(prevMap => ({
        ...prevMap,
        [CandidateId]: data
      }));
    }, errMessage => {
      showErrorToast(errMessage);
      router.back();
    }, setIsLoading);
  };

  // Provide Candidates-related data and functions through the context
  return <CandidateContext.Provider value={{
    error,
    map,
    list,
    setList,
    setMap,
    metaData,
    adjustTotal,
    getAllCandidates,
    updateCandidate,
    deleteCandidate,
    getOneCandidate
  }}>
            {children}
        </CandidateContext.Provider>;
};

// Export the context, provider component, and custom hook
export { CandidateContext, CandidateProvider, useCandidateContext };